import React, { useState, useEffect, useMemo, useCallback } from "react";
import { EditableAlertProps } from "./types";
// @ts-ignore
import debounce from "lodash.debounce";

const EditableAlerts = ({
  editable,
  alert,
  alertType,
  playerId,
  updateAlert,
  updateAlertStatus,
  updateSpeed,
  teamId,
}: EditableAlertProps) => {
  const [currentTagId, setCurrentTagId] = useState(alert.value);
  const [currentTagState, setCurrentTagState] = useState(
    +alert.value === 0 || alert.value === "" ? false : alert.active
  );

  useEffect(() => {
    setCurrentTagId(alert.value);
    setCurrentTagState(alert.active);
  }, [alert.value, alert.active, editable]);

  const commitValue = useMemo(
    () =>
      debounce((value: string) => {
        const reset = value === "" || +value === 0;
        updateAlert(playerId, alertType, reset ? { value, active: false } : { value });
        if (reset) setCurrentTagState(false);

        if (alertType === "speedMPH" && updateSpeed) {
          updateSpeed({
            stat: "speedMPH",
            teamId,
            nid: playerId.toString(),
            type: "cmon",
            target: +value,
            active: false,
          });
        }
      }, 300),
    [updateAlert, playerId, alertType, teamId, updateSpeed]
  );

  useEffect(() => () => commitValue.flush(), [commitValue]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;

    if (inputValue === "") {
      setCurrentTagId("");
      commitValue("");
      return;
    }

    const value = Number(inputValue.replace(/[^\d-]/g, ""));
    if (Number.isNaN(value) || value === 0) return;

    const finalValue =
      alertType === "maxSustDecel"
        ? String(-Math.abs(value))
        : String(Math.abs(value));

    setCurrentTagId(finalValue);
    commitValue(finalValue);
  };

  const handleStatusChange = useCallback(
    (status: boolean) => {
      if (editable) {
        setCurrentTagState(status);
        updateAlert(playerId, alertType, { active: status });
        if (alertType === "speedMPH" && updateSpeed) {
          updateSpeed({
            stat: "speedMPH",
            teamId,
            nid: playerId.toString(),
            type: "cmon",
            target: +currentTagId,
            active: status,
          });
        }
      } else {
        updateAlertStatus(status, playerId, alertType);
      }
    },
    [editable, playerId, alertType, currentTagId, updateAlert, updateAlertStatus, updateSpeed, teamId]
  );

  const isEmpty = +currentTagId === 0 || currentTagId === "";
  const base = { fontSize: "20px", fontWeight: "bolder" as const };

  const statusButtons = (
    <>
      <button
        className="btn-tag-edit-save"
        onClick={() => handleStatusChange(true)}
        disabled={isEmpty}
      >
        <i
          className="mdi mdi-check"
          style={{ ...base, color: !isEmpty && currentTagState ? "green" : "grey" }}
        />
      </button>
      <button
        className="btn-tag-edit-close"
        onClick={() => handleStatusChange(false)}
      >
        <i
          className="mdi mdi-close"
          style={{ ...base, color: isEmpty || !currentTagState ? "red" : "grey" }}
        />
      </button>
    </>
  );

  return editable ? (
    <label className="pp-ww">
      <input
        type="text"
        className="input-tag-entities"
        value={currentTagId}
        onChange={handleInputChange}
        maxLength={7}
      />
      {statusButtons}
    </label>
  ) : (
    <>
      <label
        className="pp-ww"
        style={{
          height: "30px",
          paddingTop: "6px",
          width: "62px",
          display: "inline-block",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          textAlign: "center",
        }}
      >
        {currentTagId ? currentTagId + "\n" : "-"}
      </label>
      {statusButtons}
    </>
  );
};

export default React.memo(EditableAlerts);